import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MdAddPhotoAlternate, MdClose, MdDelete, MdDescription } from 'react-icons/md';

import Layout from '../Layout';
import {
  enregistrerDommage, listerDommages, nettoyer, supprimerDommage,
  supprimerPhoto, televerserPhoto,
} from '../api';

const ELEMENTS = [
  ['PLAFOND', 'Plafond'], ['MUR', 'Mur'], ['SOL', 'Sol'],
  ['MENUISERIE', 'Menuiserie (porte, fenêtre)'], ['TOITURE', 'Toiture'],
  ['FACADE', 'Façade'], ['INSTALLATION', 'Installation technique'],
  ['MOBILIER', 'Mobilier'], ['AUTRE', 'Autre'],
];

/** Seuls ces champs acceptent null ; les autres attendent une chaîne vide. */
const NUMERIQUES = ['length', 'width', 'height', 'area'];

const VIDE = {
  piece: '', element: '', composition: '', description: '',
  length: '', width: '', height: '', area: '',
};

/**
 * Vignette d'une pièce jointe.
 *
 * Une photo se reconnaît à son image ; un devis ou une facture à son émetteur
 * et à sa référence, jamais à son nom de fichier.
 */
function Vignette({ piece, onSupprime }) {
  const doc = piece.document;
  return (
    <Card variant="outlined" sx={{ width: 168, position: 'relative' }}>
      <IconButton
        size="small" aria-label="Retirer cette pièce"
        onClick={() => onSupprime(piece.id)}
        sx={{
          position: 'absolute', top: 4, right: 4, zIndex: 1,
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'background.paper' },
        }}
      >
        <MdClose size={16} />
      </IconButton>

      {piece.apercu ? (
        <CardMedia component="img" image={piece.apercu} alt={piece.title}
                   sx={{ height: 118, objectFit: 'cover' }} />
      ) : (
        <Box sx={{
          height: 118, display: 'flex', alignItems: 'center',
          justifyContent: 'center', bgcolor: 'action.hover',
          color: 'text.secondary',
        }}>
          <MdDescription size={34} />
        </Box>
      )}

      <Box sx={{ p: 1.2 }}>
        {doc ? (
          <>
            <Typography variant="body2" noWrap title={doc.entreprise}
                        sx={{ fontWeight: 600 }}>
              {doc.entreprise || piece.title}
            </Typography>
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block' }}>
              {doc.type === 'devis' ? 'Devis' : 'Facture'}
              {doc.reference ? ` ${doc.reference}` : ''}
            </Typography>
            {doc.date && (
              <Typography variant="caption" color="text.secondary">
                {doc.date}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" noWrap title={piece.title}>
            {piece.title || 'pièce jointe'}
          </Typography>
        )}
      </Box>
    </Card>
  );
}

/** Une fiche de dommage, dépliée pour saisie. */
function Fiche({ dommage, expertiseId, onEnregistre, onSupprime }) {
  const [champs, setChamps] = useState(() => ({
    ...VIDE,
    ...Object.fromEntries(
      Object.keys(VIDE).map((k) => [k, dommage?.[k] ?? '']),
    ),
  }));
  const [photos, setPhotos] = useState(dommage?.photos || []);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const champFichier = useRef(null);

  const modifier = (nom) => (e) =>
    setChamps((c) => ({ ...c, [nom]: e.target.value }));

  const enregistrer = async () => {
    if (!champs.element) {
      setErreur('Indiquez au moins quel élément est touché.');
      return null;
    }
    setOccupe(true);
    setErreur('');
    try {
      const charge = nettoyer(champs, NUMERIQUES);
      const enregistre = await enregistrerDommage(expertiseId, charge, dommage?.id);
      onEnregistre(enregistre);
      return enregistre;
    } catch {
      setErreur("L'enregistrement n'a pas abouti.");
      return null;
    } finally {
      setOccupe(false);
    }
  };

  const ajouterPhotos = async (fichiers) => {
    // Le dommage doit exister pour porter ses photos.
    const cible = dommage?.id ? dommage : await enregistrer();
    if (!cible?.id) return;
    setOccupe(true);
    try {
      const ajoutees = [];
      for (const f of fichiers) {
        ajoutees.push(await televerserPhoto(expertiseId, cible.id, f));
      }
      setPhotos((p) => [...p, ...ajoutees]);
    } catch {
      setErreur("Le dépôt d'une photo a échoué.");
    } finally {
      setOccupe(false);
    }
  };

  const retirerPiece = async (fileId) => {
    setOccupe(true);
    try {
      await supprimerPhoto(fileId);
      setPhotos((p) => p.filter((x) => x.id !== fileId));
    } catch {
      setErreur("La suppression n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 } }}>
      <Stack spacing={3}>
        {erreur && <Alert severity="error">{erreur}</Alert>}

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <TextField select label="Qu'est-ce qui est touché ?"
                     value={champs.element} onChange={modifier('element')}>
            {ELEMENTS.map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
          </TextField>
          <TextField label="Dans quelle pièce ?" value={champs.piece}
                     onChange={modifier('piece')}
                     placeholder="cuisine, salon, chambre…" />
        </Stack>

        <TextField
          label="En quoi est-ce fait ?" value={champs.composition}
          onChange={modifier('composition')}
          placeholder="plaque de plâtre peinte, parquet chêne collé, carrelage…"
          helperText="C'est le renseignement le plus utile : il décide du type de réparation proposé."
        />

        <TextField label="Décrivez les dégâts" multiline minRows={2}
                   value={champs.description} onChange={modifier('description')}
                   placeholder="des auréoles d'eau sont apparues, la peinture cloque…" />

        <Divider />
        <Typography variant="body2" color="text.secondary">
          Dimensions — renseignez la surface si vous la connaissez, sinon la
          longueur et la largeur suffisent.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField label="Longueur" type="number" value={champs.length}
                     onChange={modifier('length')} InputProps={{ endAdornment: 'm' }} />
          <TextField label="Largeur" type="number" value={champs.width}
                     onChange={modifier('width')} InputProps={{ endAdornment: 'm' }} />
          <TextField label="Hauteur" type="number" value={champs.height}
                     onChange={modifier('height')} InputProps={{ endAdornment: 'm' }} />
          <TextField label="Surface" type="number" value={champs.area}
                     onChange={modifier('area')} InputProps={{ endAdornment: 'm²' }} />
        </Stack>

        <Divider />
        <Stack spacing={2}>
          {photos.length > 0 && (
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {photos.map((p) => (
                <Vignette key={p.id} piece={p} onSupprime={retirerPiece} />
              ))}
            </Stack>
          )}
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap"
                 useFlexGap>
            <input ref={champFichier} type="file"
                   accept="image/*,application/pdf" multiple hidden
                   onChange={(e) => ajouterPhotos([...e.target.files])} />
            <Button startIcon={<MdAddPhotoAlternate />} disabled={occupe}
                    onClick={() => champFichier.current?.click()}>
              Ajouter des photos ou un document
            </Button>
            {photos.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Photographiez la zone abîmée, d'assez près pour qu'on distingue le matériau.
              </Typography>
            )}
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end">
          {dommage?.id && (
            <IconButton aria-label="Supprimer ce dommage" disabled={occupe}
                        onClick={() => onSupprime(dommage.id)}>
              <MdDelete />
            </IconButton>
          )}
          <Button variant="outlined" onClick={enregistrer} disabled={occupe}>
            Enregistrer ce dommage
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * Troisième étape : l'inventaire des dommages, avec leurs photos.
 *
 * C'est l'écran qui porte le plus de valeur : la composition saisie ici décide
 * du type de réparation proposé ensuite.
 */
export default function Dommages({ expertiseId, onSuivant, onRetour, onEtape }) {
  const [dommages, setDommages] = useState(null);
  const [nouveau, setNouveau] = useState(false);
  const [erreur, setErreur] = useState('');

  const recharger = useCallback(() => {
    listerDommages(expertiseId)
      .then(setDommages)
      .catch(() => setErreur('Impossible de charger vos dommages.'));
  }, [expertiseId]);

  useEffect(recharger, [recharger]);

  const apresEnregistrement = () => { setNouveau(false); recharger(); };

  const supprimer = async (id) => {
    await supprimerDommage(id);
    recharger();
  };

  return (
    <Layout
      etape="dommages"
      onEtape={onEtape}
      titre="Vos dommages"
      introduction="Décrivez chaque zone abîmée séparément — un plafond et un mur sont deux dommages, même dans la même pièce."
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large" onClick={onSuivant}
                  disabled={!dommages?.length}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

      <Stack spacing={3}>
        {dommages === null && <CircularProgress size={28} />}

        {dommages?.map((d) => (
          <Fiche key={d.id} dommage={d} expertiseId={expertiseId}
                 onEnregistre={apresEnregistrement} onSupprime={supprimer} />
        ))}

        {(nouveau || dommages?.length === 0) && (
          <Fiche expertiseId={expertiseId} onEnregistre={apresEnregistrement}
                 onSupprime={() => setNouveau(false)} />
        )}

        {!nouveau && dommages?.length > 0 && (
          <Button variant="outlined" onClick={() => setNouveau(true)}>
            Ajouter un autre dommage
          </Button>
        )}
      </Stack>
    </Layout>
  );
}
